import { Injectable, inject } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';
import { Storage } from '@angular/fire/storage';
import { doc, getDoc } from 'firebase/firestore';
import { getBlob, ref, uploadBytes } from 'firebase/storage';
import { AccessRight } from '../../../../lib/models/oAuth.model';
import { VersionType } from '../../../../lib/services/access-matrix.service';
import { Credentials } from '../../../../lib/models/credentials.model';
import {
  FirestoreAccessRightDocument,
  FirestoreConfigDocument,
} from '../model/persistence.model';

@Injectable({
  providedIn: 'root',
})
export class PersistenceService {
  private readonly firestore = inject(Firestore);
  private readonly storage = inject(Storage);

  public async getAccessRight(
    userEmail: string,
  ): Promise<AccessRight | undefined> {
    const document = await this.getDocument(userEmail);
    if (!document) {
      return undefined;
    }

    // Use former model to keep the rest of the application compatible
    // By adding the VersionType available, we authorized all valid users to
    // both environment (FULL or FREE)
    return {
      user: userEmail,
      rights: [
        {
          version: VersionType.FULL,
          options: this.getOptions(document),
        },
        {
          version: VersionType.FREE,
          options: this.getOptions(document),
        },
      ],
    };
  }

  public async getCredentials(
    type: VersionType,
  ): Promise<Credentials | undefined> {
    const document = await this.getConfigDocument();

    let username: string | undefined = undefined;
    let password: string | undefined = undefined;

    if (type === VersionType.FULL) {
      username = document?.icureFullUsername;
      password = document?.icureFullPassword;
    }

    if (type === VersionType.FREE) {
      username = document?.icureFreeUsername;
      password = document?.icureFreePassword;
    }

    if (!username || !password) {
      return undefined;
    }

    const credentials: Credentials = new Credentials({
      username,
      password,
    });

    return credentials;
  }

  public async putPublicFileObject(
    filePath: string,
    fileObject: Blob | Uint8Array | ArrayBuffer,
  ): Promise<void> {
    const fileReference = this.getPublicFileReference(filePath);

    await uploadBytes(fileReference, fileObject);
  }

  public async putPublicFileText(
    filePath: string,
    content: string,
  ): Promise<void> {
    await this.putPublicFileObject(
      filePath,
      new Blob([content], { type: 'text/html' }),
    );
  }

  public async getPublicFileObject(filePath: string): Promise<Blob> {
    const fileReference = this.getPublicFileReference(filePath);

    return await getBlob(fileReference);
  }

  public async getPublicFileText(filePath: string): Promise<string> {
    try {
      const fileObject: Blob = await this.getPublicFileObject(filePath);

      if (typeof fileObject.text === 'function') {
        return await fileObject.text();
      }

      return await new Response(fileObject).text();
    } catch {
      return '';
    }
  }

  private async getDocument(
    userEmail: string,
  ): Promise<FirestoreAccessRightDocument | undefined> {
    const userId = userEmail;
    const documentRef = doc(this.firestore, 'users', userId);
    const snapshot = await getDoc(documentRef);

    if (!snapshot.exists()) {
      return undefined;
    }

    return snapshot.data() as FirestoreAccessRightDocument;
  }

  private async getConfigDocument(): Promise<
    FirestoreConfigDocument | undefined
  > {
    const documentRef = doc(this.firestore, 'config', 'secrets');
    const snapshot = await getDoc(documentRef);

    if (!snapshot.exists()) {
      return undefined;
    }

    return snapshot.data() as FirestoreConfigDocument;
  }

  private getPublicFileReference(filePath: string): ReturnType<typeof ref> {
    return ref(this.storage, `public/${filePath}`);
  }

  private getOptions(accessRight: FirestoreAccessRightDocument): string[] {
    const options: string[] = [];

    if (accessRight.privilegeGroupCreation === true) {
      options.push('groupCreation');
    }

    if (accessRight.privilegeAppSettingsAction === true) {
      options.push('appSettingsAction');
    }

    if (accessRight.privilegeBatchWriteAction === true) {
      options.push('batchWriteAction');
    }

    if (accessRight.privilegeLicensesAction === true) {
      options.push('licensesAction');
    }

    if (accessRight.privilegeLicensesOptions === true) {
      options.push('licensesOptions');
    }

    if (accessRight.privilegeBannerAction === true) {
      options.push('bannerAction');
    }

    return options;
  }
}
